import React, { useEffect, useState, useCallback } from 'react';
import { Link, Navigate, useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Paper,
  TextField,
  Button,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography
} from '@mui/material';
import { platformApiFetch } from './platformApi';
import { usePlatformAuth } from '../context/PlatformAuthContext';

const STATUSES = ['draft', 'published', 'archived'];
const VIS = ['public', 'authenticated', 'platform'];

function toDatetimeLocalValue(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function fromDatetimeLocalValue(s) {
  if (!s || !String(s).trim()) return null;
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return null;
  return d.toISOString();
}

export default function PlatformKnowledgeArticleFormPage() {
  const { articleId } = useParams();
  const isEdit = Boolean(articleId);
  const navigate = useNavigate();
  const { user, ready } = usePlatformAuth();
  const isSuper = user?.role === 'super_admin';

  const [title, setTitle] = useState('');
  const [slug, setSlug] = useState('');
  const [category, setCategory] = useState('general');
  const [summary, setSummary] = useState('');
  const [content, setContent] = useState('');
  const [status, setStatus] = useState('draft');
  const [visibility, setVisibility] = useState('public');
  const [publishedAt, setPublishedAt] = useState('');
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    if (!isEdit) return;
    setLoading(true);
    setError(null);
    try {
      const res = await platformApiFetch(`/api/platform/knowledge-base/articles/${articleId}`);
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json.error || 'Failed to load');
      const a = json.article;
      setTitle(a.title || '');
      setSlug(a.slug || '');
      setCategory(a.category || 'general');
      setSummary(a.summary || '');
      setContent(a.content || '');
      setStatus(a.status || 'draft');
      setVisibility(a.visibility || 'public');
      setPublishedAt(toDatetimeLocalValue(a.published_at));
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [isEdit, articleId]);

  useEffect(() => {
    load();
  }, [load]);

  if (!ready) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!isSuper) {
    return <Navigate to={isEdit ? `/platform/knowledge-base/articles/${articleId}` : '/platform/knowledge-base/articles'} replace />;
  }

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    try {
      const body = {
        title: title.trim(),
        category: category.trim() || 'general',
        summary: summary.trim() || null,
        content: content.trim(),
        status,
        visibility,
        published_at: fromDatetimeLocalValue(publishedAt)
      };
      if (slug.trim()) body.slug = slug.trim().toLowerCase();

      if (isEdit) {
        const res = await platformApiFetch(`/api/platform/knowledge-base/articles/${articleId}`, {
          method: 'PATCH',
          body
        });
        const json = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(json.error || 'Save failed');
        navigate(`/platform/knowledge-base/articles/${articleId}`);
      } else {
        const res = await platformApiFetch('/api/platform/knowledge-base/articles', {
          method: 'POST',
          body
        });
        const json = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(json.error || 'Create failed');
        navigate(`/platform/knowledge-base/articles/${json.article.id}`);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h5" gutterBottom>
        {isEdit ? 'Edit global article' : 'New global article'}
      </Typography>
      <Button variant="outlined" component={Link} to={isEdit ? `/platform/knowledge-base/articles/${articleId}` : '/platform/knowledge-base/articles'} sx={{ mb: 2 }}>
        Cancel
      </Button>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      <Paper sx={{ p: 2, maxWidth: 900 }}>
        <Box component="form" onSubmit={save}>
          <TextField fullWidth required label="Title" value={title} onChange={(e) => setTitle(e.target.value)} margin="normal" />
          <TextField
            fullWidth
            label="Slug"
            value={slug}
            onChange={(e) => setSlug(e.target.value)}
            margin="normal"
            helperText="Unique among global articles. Leave empty on create to derive from title."
          />
          <TextField fullWidth label="Category" value={category} onChange={(e) => setCategory(e.target.value)} margin="normal" />
          <TextField fullWidth label="Summary" value={summary} onChange={(e) => setSummary(e.target.value)} margin="normal" multiline minRows={2} />
          <TextField fullWidth required label="Content" value={content} onChange={(e) => setContent(e.target.value)} margin="normal" multiline minRows={12} />
          <FormControl fullWidth margin="normal">
            <InputLabel>Status</InputLabel>
            <Select label="Status" value={status} onChange={(e) => setStatus(e.target.value)}>
              {STATUSES.map((s) => (
                <MenuItem key={s} value={s}>
                  {s}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth margin="normal">
            <InputLabel>Visibility</InputLabel>
            <Select label="Visibility" value={visibility} onChange={(e) => setVisibility(e.target.value)}>
              {VIS.map((v) => (
                <MenuItem key={v} value={v}>
                  {v}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            fullWidth
            label="Published at"
            type="datetime-local"
            value={publishedAt}
            onChange={(e) => setPublishedAt(e.target.value)}
            margin="normal"
            InputLabelProps={{ shrink: true }}
          />
          <Box sx={{ mt: 2 }}>
            <Button type="submit" variant="contained" disabled={saving || !title.trim() || !content.trim()}>
              {saving ? 'Saving…' : isEdit ? 'Save' : 'Create'}
            </Button>
          </Box>
        </Box>
      </Paper>
    </Box>
  );
}
